import SignUpCarouselDesktop from "@components/carousel/SignUpCarouselDesktop";
import React, { useEffect, useRef, useState } from "react";

import Logo from "@assets/logos/logo.svg";
import PaySonicLogo from "@assets/logos/PaySonic.svg";
import PaysonicNoise from "@assets/noise/paysonic-noise.svg";
import SignUpNoise from "@assets/noise/tylt-noise.svg";
import CustomButton from "@components/buttons/CustomButton";
import LightCustomButton from "@components/buttons/LightCustomButton";
import { getApiErrorDisplay, getRawApiMessage } from "@services/api/apiError";
import { authenticationApiService } from "@services/apis/Authentication";
import { FormProvider, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import secureLocalStorage from "react-secure-storage";
import {
  isPaysonicHost,
  isPrimeBrazilOnlyHost,
} from "src/config/deploymentConfig";
import { OTPFormField } from "./components/otp/OTPFormField/OTPFormField";

const VerifyOtp = () => {
  const methods = useForm({
    defaultValues: {
      otp: Array(6).fill(""), // Default value for the otp field
    },
  });

  const navigate = useNavigate();
  const marketingDataString = sessionStorage.getItem("marketingData");
  const marketingData = marketingDataString
    ? JSON.parse(marketingDataString)
    : "";

  console.log("marketingData", marketingData);
  const location = useLocation();
  let email = (location.state as { email?: string })?.email ?? "";
  let csrfToken = (location.state as { csrfToken?: string })?.csrfToken ?? "";
  console.log("email", email);
  console.log("csrfToken", csrfToken);

  const loginEmail = email ? secureLocalStorage.getItem("loginEmail") : "";
  const loginCsrfToken = csrfToken
    ? secureLocalStorage.getItem("loginCsrfToken")
    : "";

  console.log("loginEmail", loginEmail);
  console.log("loginCsrfToken", loginCsrfToken);

  useEffect(() => {
    if (!loginEmail || !loginCsrfToken) {
      navigate("/login");
    }
  }, [loginEmail, loginCsrfToken, navigate]);
  const validLoginCsrfToken =
    typeof loginCsrfToken === "string" ? loginCsrfToken : "";

  const [token, setToken] = useState<string>(validLoginCsrfToken || csrfToken);

  const continueButtonRef = useRef<HTMLButtonElement>(null);
  const [seconds, setSeconds] = useState(30);
  const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false);

  const isPrimeBrazilOnly = isPrimeBrazilOnlyHost();
  const isPaysonic = isPaysonicHost();

  const timeSetting = () => {
    // Clear any existing interval when setting a new one
    const interval = setInterval(() => {
      setSeconds((prevSeconds) => {
        if (prevSeconds <= 1) {
          clearInterval(interval); // Clear interval when reaching 0
          return 0; // Set seconds to 0 and stop
        }
        return prevSeconds - 1;
      });
    }, 1000);

    // Return the interval ID to clear it later if needed
    return interval;
  };

  useEffect(() => {
    const interval = timeSetting();

    // Cleanup the interval on component unmount or when re-renders
    return () => clearInterval(interval);
  }, []);

  const handleResendClick = () => {
    console.log("click");

    // Clear OTP fields
    methods.reset({
      otp: Array(6).fill(""), // Reset OTP to an array of empty strings
    });

    // Clear any existing OTP errors
    clearErrors("otp");

    // Reset the timer
    setSeconds(30);
    timeSetting();
    handleResend();
  };

  const {
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
  } = methods;

  // Function to handle form submit
  const onSubmit = async (data: { otp: any[] }) => {
    setIsButtonLoading(true);

    const code = data.otp.join("");
    console.log("code", code);

    try {
      const response = await authenticationApiService.verifyOtp(
        email,
        code,
        token,
      );
      console.log("verify otp response", response);
      secureLocalStorage.setItem("jwt", response.data.jwt);
      console.log("login successful");
      secureLocalStorage.removeItem("loginEmail");
      secureLocalStorage.removeItem("loginCsrfToken");
      navigate("/");
      window.location.reload();
    } catch (error: any) {
      const msg = getRawApiMessage(error);
      if (msg === "Access Denied!") {
        navigate("/error");
      } else {
        handleSetOtpErrorMessage(getApiErrorDisplay(error));
      }
    } finally {
      setIsButtonLoading(false);
    }
  };

  // Optional: handle error messages for OTP from within OTPFormField
  const handleSetOtpErrorMessage = (message: any) => {
    if (message) {
      setError("otp", {
        type: "manual",
        message,
      });
    } else {
      clearErrors("otp");
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      continueButtonRef.current?.click();
    }
  };

  const checkValidOtp = () => {
    const data = methods.getValues();
    console.log("data", data);
    const code = data.otp.join("");
    return code.length === 6;
  };

  const handleResend = async () => {
    setIsButtonLoading(true);

    try {
      const data = await authenticationApiService.sendOtp(email);
      console.log("csrf", data.data.csrfToken);
      setToken(data.data.csrfToken);
      // localStorage.setItem("loginCsrfToken", data.data.csrfToken);
    } catch (error: any) {
      handleSetOtpErrorMessage(getApiErrorDisplay(error));
    } finally {
      setIsButtonLoading(false);
    }
  };

  const checkLogin = () => {
    const jwt = secureLocalStorage.getItem("jwt");
    if (jwt) {
      navigate("/");
    }
  };

  useEffect(() => {
    checkLogin();
  }, []);

  return (
    <div className="flex bg-[#ffffff] min-h-screen">
      <div
        className="w-[50vw]  bg-text-navbar-active pt-24"
        style={{
          backgroundImage: `url(${isPaysonic ? PaysonicNoise : SignUpNoise})`,
        }}
      >
        <SignUpCarouselDesktop />
      </div>
      <div className="w-[50vw] flex flex-col justify-start mt-24 items-center">
        <div className="w-3/4 flex flex-row mb-[40px] items-center justify-center">
          {isPrimeBrazilOnly ? (
            <div className="h-44 w-44" />
          ) : (
            <img
              src={isPaysonic ? PaySonicLogo : Logo}
              alt="logo"
              className="h-44 w-44"
            />
          )}
        </div>
        <div className="p-[20px] w-3/4 rounded-[12px]">
          <div className="flex items-center justify-between mb-4 relative">
            {/* Back Button - Positioned Absolutely to the Left */}
            <span
              className="material-symbols-outlined  absolute left-0 cursor-pointer"
              onClick={() => navigate("/login")}
            >
              arrow_back_ios
            </span>

            {/* Centered Title */}
            <div className="text-text-base text-[14px] font-semibold flex-grow text-center">
              Start your admin journey
            </div>
          </div>

          <div className="text-text-base text-[14px] mt-4 font-normal mb-4">
            Enter the verification code we’ve sent to your email ID
            <br />
            {email}.
          </div>
          <div className="border-[0.5px] border-border-table"></div>

          <FormProvider {...methods}>
            <form onKeyDown={handleKeyDown}>
              <OTPFormField
                name="otp"
                onSetOtpErrorMessage={handleSetOtpErrorMessage}
                length={6}
                errorMessage={errors.otp && errors.otp.message}
              />
              <div className="flex justify-between items-center mt-3">
                <span className="text-[12px] font-light">
                  Enter OTP in{" "}
                  <span className="text-text-brand-alt font-semibold text-[14px]">
                    {seconds}s
                  </span>
                </span>
                <div className="mt-2">
                  <LightCustomButton
                    text="REGENERATE OTP"
                    onClick={handleResendClick}
                    isActive={seconds <= 0}
                    isButtonLoading={isButtonLoading}
                  />
                </div>
              </div>
            </form>
          </FormProvider>
          <div className="mt-7 my-4">
            <CustomButton
              ref={continueButtonRef}
              text="SUBMIT"
              onClick={handleSubmit(onSubmit)}
              isActive={checkValidOtp()}
              isButtonLoading={isButtonLoading}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyOtp;
